import { getOpenCV } from "./opencv";

import { debugLog } from "../../../utils/debug";

function matPoint(mat, index) {
  return {
    x: mat.data32F[index * 2],
    y: mat.data32F[index * 2 + 1],
  };
}

// Semi-dense Shi-Tomasi features tracked with pyramidal Lucas-Kanade flow.
export async function trackSemiDenseFeatures(
  imageElementA,
  imageElementB,
  {
    maxCorners = 8000,
    qualityLevel = 0.002,
    minDistance = 3,
    forwardBackwardThreshold = 4.0,
  } = {},
) {
  const cv = await getOpenCV();

  debugLog("[Reconstruction] Dense OpenCV capabilities:", {
    goodFeaturesToTrack: typeof cv.goodFeaturesToTrack,
    calcOpticalFlowPyrLK: typeof cv.calcOpticalFlowPyrLK,
    GFTTDetector: typeof cv.GFTTDetector,
    FastFeatureDetector: typeof cv.FastFeatureDetector,
    ORB: typeof cv.ORB,
  });

  const sourceA = cv.imread(imageElementA);
  const sourceB = cv.imread(imageElementB);

  const grayA = new cv.Mat();
  const grayB = new cv.Mat();

  const cornersA = new cv.Mat();
  const trackedB = new cv.Mat();
  const statusForward = new cv.Mat();
  const errorForward = new cv.Mat();

  const trackedBackA = new cv.Mat();
  const statusBackward = new cv.Mat();
  const errorBackward = new cv.Mat();

  try {
    cv.cvtColor(sourceA, grayA, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(sourceB, grayB, cv.COLOR_RGBA2GRAY);

    const detector = new cv.GFTTDetector(maxCorners, qualityLevel, minDistance);
    const keypoints = new cv.KeyPointVector();

    detector.detect(grayA, keypoints);

    // Lucas-Kanade expects point coordinates as a CV_32FC2 matrix.
    const pointData = new Float32Array(keypoints.size() * 2);

    for (let i = 0; i < keypoints.size(); i += 1) {
      const keypoint = keypoints.get(i);

      pointData[i * 2] = keypoint.pt.x;
      pointData[i * 2 + 1] = keypoint.pt.y;
    }

    const detectedPoints = cv.matFromArray(
      keypoints.size(),
      1,
      cv.CV_32FC2,
      pointData,
    );

    detectedPoints.copyTo(cornersA);

    keypoints.delete();
    detector.delete();

    if (cornersA.rows === 0) {
      return [];
    }

    const windowSize = new cv.Size(21, 21);

    const criteria = new cv.TermCriteria(
      cv.TermCriteria_EPS | cv.TermCriteria_COUNT,
      30,
      0.01,
    );

    // Forward tracking: A -> B.
    cv.calcOpticalFlowPyrLK(
      grayA,
      grayB,
      cornersA,
      trackedB,
      statusForward,
      errorForward,
      windowSize,
      3,
      criteria,
    );

    // Backward tracking: B -> A for consistency checking.
    cv.calcOpticalFlowPyrLK(
      grayB,
      grayA,
      trackedB,
      trackedBackA,
      statusBackward,
      errorBackward,
      windowSize,
      3,
      criteria,
    );

    const tracks = [];

    for (let i = 0; i < cornersA.rows; i += 1) {
      if (statusForward.data[i] === 0 || statusBackward.data[i] === 0) {
        continue;
      }

      const pointA = matPoint(cornersA, i);
      const pointB = matPoint(trackedB, i);
      const returnedA = matPoint(trackedBackA, i);

      if (!Number.isFinite(pointB.x) || !Number.isFinite(pointB.y)) {
        continue;
      }

      if (
        pointB.x < 0 ||
        pointB.y < 0 ||
        pointB.x >= sourceB.cols ||
        pointB.y >= sourceB.rows
      ) {
        continue;
      }

      // A -> B -> A should return close to the original point.
      const dx = returnedA.x - pointA.x;
      const dy = returnedA.y - pointA.y;
      const forwardBackwardError = Math.sqrt(dx * dx + dy * dy);

      if (forwardBackwardError > forwardBackwardThreshold) {
        continue;
      }

      tracks.push({
        pointA,
        pointB,
        trackingError: errorForward.data32F[i],
        forwardBackwardError,
      });
    }

    debugLog("[Reconstruction] Semi-dense tracking:", {
      detected: cornersA.rows,
      accepted: tracks.length,
    });

    return tracks;
  } finally {
    sourceA.delete();
    sourceB.delete();

    grayA.delete();
    grayB.delete();

    cornersA.delete();
    trackedB.delete();

    statusForward.delete();
    errorForward.delete();

    trackedBackA.delete();
    statusBackward.delete();
    errorBackward.delete();
  }
}
